/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocale } from "../i18n/hooks";
import { useAppSelector } from "../../app/hooks";
import {
  selectAction,
  selectItemId,
  selectShowModal,
  setShowModal,
} from "./deleteModalSlice";
import { useDispatch } from "react-redux";
import "./deleteModal.scss";
import { useParams } from "react-router-dom";
import {
  Card,
  Lesson,
  Resource,
  Sublesson,
  UserResource,
} from "../resources/types";
import { deleteCard, deleteLesson, deleteTab } from "./deleteFunctions";
import BLSpinner from "../../assets/animations/BLSpinner/BLSpinner";
import { ReactComponent as ExclamationCircle } from "bootstrap-icons/icons/exclamation-circle.svg";
import HelpLink from "../helpLink/HelpLink";
import { cardsApi } from "../../app/services/resource/cardsApi";
import { userCardsApi } from "../../app/services/userResource/userCardsApi";

type Props = {
  resource: UserResource | Resource;
};

const DeleteModal: React.FC<Props> = ({ resource }) => {
  const { t: _ } = useTranslation();
  const locale = useLocale();
  const showModal = useAppSelector(selectShowModal());
  const action = useAppSelector(selectAction());
  const dispatch = useDispatch();
  const itemId = useAppSelector(selectItemId());

  const [loading, setLoading] = useState<boolean>(false);
  const [checking, setChecking] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const { lessonId, pageId } = useParams();

  const lessonIndex = Number(lessonId);
  const sublessonIndex = Number(pageId);

  // Check if a sublesson has cards linked to it
  // Filter out any ghost cards
  const checkForCards = useCallback(
    async (sublesson: Sublesson) => {
      const cardUuids = sublesson?.cards
        .filter((item) => item.originalUUID !== "")
        ?.map((item) => item.uuid);

      const { Code, Locale, Copy } = resource as UserResource;

      const { data: resourceCards } =
        cardUuids && cardUuids.length > 0
          ? await dispatch(
              cardsApi.endpoints.getResourceSubLessonCards.initiate({
                resourceId: Code,
                subLessonUuid: sublesson.uuid,
                cardUuids,
              }),
            )
          : { data: undefined };

      const { data: userResourceCards } = await dispatch(
        userCardsApi.endpoints.getUserResourceSubLessonCards.initiate({
          resourceId: Code,
          locale: Locale,
          copy: Copy,
          subLessonUuid: sublesson.uuid,
        }),
      );

      const cards = (resourceCards ?? []).concat(userResourceCards ?? []);
      const cardOrder = [...(sublesson?.cards ?? [])].map((item) => item.uuid);

      return (
        [...cards].filter((card: Card) => cardOrder.includes(card.UUID))
          .length > 0
      );
    },
    [dispatch, resource],
  );

  useEffect(() => {
    const checkLessonCards = async () => {
      setChecking(true);
      const lesson = resource.Object.lessons.find(
        (lesson: Lesson) => lesson.uuid === itemId,
      );
      setDisabled(true);

      if (lesson?.subLessons?.length) {
        // Await all checks
        const results = await Promise.all(
          lesson.subLessons.map((sublesson: Sublesson) =>
            checkForCards(sublesson),
          ),
        );
        const hasCards = results.some(Boolean);

        setChecking(false);
        if (hasCards) {
          setDisabled(true);
          setError(_("deleteModal.sublessonHasCards"));
        } else {
          setDisabled(false);
          setError(undefined);
        }
      } else {
        setDisabled(false);
        setError(undefined);
      }
    };

    if (action === "lesson") checkLessonCards();

    return () => {
      setDisabled(false);
      setError(undefined);
      setLoading(false);
    };
  }, [action, resource, itemId, _, checkForCards]);

  const deleteAction = async () => {
    if (!action) return;

    if (action === "card") {
      await deleteCard(resource, lessonIndex, sublessonIndex, itemId);
    }
    if (["download", "hint", "answer", "extension"].includes(action)) {
      await deleteTab(
        resource,
        lessonIndex,
        sublessonIndex,
        itemId,
        action,
        locale,
      );
    }
    if (action === "lesson") {
      await deleteLesson(resource, itemId, locale);
    }

    setLoading(false);
  };

  return (
    <Modal
      dialogClassName="deleteModal"
      show={showModal}
      onHide={() => dispatch(setShowModal(false))}
      centered
    >
      <Modal.Header closeButton>
        <span className="flex-grow-1">{_(`deleteModal.${action}`)}</span>
        <HelpLink position="inherit" className="p-0 help-link" />
      </Modal.Header>
      <Modal.Body>
        <p>
          {_("deleteModal.confirm")}
          <br />
          <strong>{_("deleteModal.cannotUndo")}</strong>
        </p>
        {checking && (
          <Alert variant="light">
            <div className="d-flex gap-2">
              <div className="mr-2">
                <Spinner size="sm" />
              </div>
              <div>{_("deleteModal.checkingSublessonCards")}</div>
            </div>
          </Alert>
        )}
        {!!error && (
          <Alert variant="danger">
            <div className="d-flex gap-2">
              <div className="mr-2">
                <ExclamationCircle height={16} className="text-danger" />
              </div>
              <div>{_("Error: {{error}}", { error: error })}</div>
            </div>
          </Alert>
        )}
        <div className="d-flex gap-3 justify-content-end">
          <Button
            className="btn-outline-editing"
            role="button"
            variant="default"
            onClick={() => dispatch(setShowModal(false))}
          >
            {_("Cancel")}
          </Button>
          <Button
            disabled={disabled}
            role="button"
            variant="danger"
            onClick={async () => {
              setLoading(true);
              await deleteAction();
              dispatch(setShowModal(false));
            }}
          >
            {loading ? <BLSpinner /> : _("Delete")}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DeleteModal;
